/**
 * Access-token refresh — invoked when the current access token is within
 * `REFRESH_LEAD_SECONDS` of its `exp`, or when an API call returns 401.
 *
 * On failure, tokens are cleared and the caller is expected to redirect
 * the user back to the cerberus auth UI.
 */
import { CERBERUS_API_URL, REFRESH_LEAD_SECONDS } from './config'
import { unsafeDecodeClaims } from './jwt'
import { redirectToLogin } from './redirect'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getUserId,
  setTokens,
} from './tokens'
import type { RefreshResponse } from './types'

export class RefreshError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'RefreshError'
    this.status = status
  }
}

interface RefreshOptions {
  apiUrl?: string
  fetchImpl?: typeof fetch
  /** Retries on 5xx. Default 2. */
  retries?: number
  /** Base backoff ms (1 << attempt). Default 200. */
  backoffMs?: number
  /** When true, on terminal failure clear tokens + redirect. Default true. */
  redirectOnFailure?: boolean
  /**
   * T8 — optional desired active domain. Forwarded to /auth/refresh as
   * `active_domain` so cerberus mints the next access token with that hint
   * baked in. Used by the domain-switcher UI.
   */
  activeDomain?: string | null
}

/**
 * Returns true if the current access token expires within `leadSeconds` from
 * `now` (defaults: REFRESH_LEAD_SECONDS, Date.now()/1000). Also returns true
 * if there is no current access token.
 */
export function shouldRefresh(
  leadSeconds: number = REFRESH_LEAD_SECONDS,
  now: number = Math.floor(Date.now() / 1000),
): boolean {
  const token = getAccessToken()
  if (!token) return true
  const claims = unsafeDecodeClaims(token)
  if (!claims || typeof claims.exp !== 'number') return true
  return claims.exp - now <= leadSeconds
}

async function sleep(ms: number): Promise<void> {
  await new Promise((r) => setTimeout(r, ms))
}

/**
 * Calls POST /auth/refresh, persists new tokens, returns them.
 *
 * Retries on 5xx with exponential backoff. On 4xx (including 401), clears
 * tokens; if `redirectOnFailure` is true, also redirects to the login UI.
 */
export async function refreshAccessToken(
  opts: RefreshOptions = {},
): Promise<RefreshResponse> {
  const apiUrl = (opts.apiUrl ?? CERBERUS_API_URL).replace(/\/$/, '')
  const fetchFn = opts.fetchImpl ?? fetch
  const retries = opts.retries ?? 2
  const backoffMs = opts.backoffMs ?? 200
  const redirectOnFailure = opts.redirectOnFailure ?? true

  const refresh_token = getRefreshToken()
  const user_id = getUserId()
  if (!refresh_token || !user_id) {
    if (redirectOnFailure) {
      clearTokens()
      redirectToLogin()
    }
    throw new RefreshError(0, 'No refresh token / user_id in storage')
  }

  // T8: forward `active_domain` only when explicitly provided. Sending null
  // or omitting it preserves cerberus' default (host inference / no change).
  const requestBody: Record<string, unknown> = { refresh_token, user_id }
  if (typeof opts.activeDomain === 'string' && opts.activeDomain.length > 0) {
    requestBody.active_domain = opts.activeDomain
  }

  let lastErr: unknown = null
  for (let attempt = 0; attempt <= retries; attempt++) {
    let res: Response
    try {
      res = await fetchFn(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      })
    } catch (err) {
      lastErr = err
      if (attempt < retries) {
        await sleep(backoffMs * (1 << attempt))
        continue
      }
      break
    }

    if (res.ok) {
      const body = (await res.json()) as RefreshResponse
      if (!body.access_token || !body.refresh_token) {
        throw new RefreshError(res.status, 'Refresh response missing tokens')
      }
      // Cerberus's /auth/refresh response intentionally omits user_id (the
      // GSI1 token lookup means the client no longer has to send it either,
      // see cerberus refresh.py _lookup_refresh_row). Reuse the user_id
      // already in storage; we know it's set because we read it above to
      // build the request body.
      const nextUserId = body.user_id ?? user_id
      setTokens({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        user_id: nextUserId,
      })
      return { ...body, user_id: nextUserId }
    }

    if (res.status >= 500) {
      if (attempt < retries) {
        await sleep(backoffMs * (1 << attempt))
        continue
      }
      // 5xx with no retries left — surface error WITHOUT nuking tokens.
      throw new RefreshError(
        res.status,
        `Refresh failed after ${retries + 1} attempts: HTTP ${res.status}`,
      )
    }

    // Terminal 4xx — refresh is no longer valid.
    clearTokens()
    if (redirectOnFailure) redirectToLogin()
    throw new RefreshError(res.status, `Refresh failed: HTTP ${res.status}`)
  }

  // All retries exhausted on transport/5xx error — don't nuke tokens, the
  // network may recover. Just surface the error to the caller.
  throw new RefreshError(
    0,
    `Refresh failed after ${retries + 1} attempts: ${
      lastErr instanceof Error ? lastErr.message : String(lastErr)
    }`,
  )
}
